// Shared vanilla-JS interactions for page-clone templates. No frameworks, no CDN scripts.
// Append new functions here as new templates need them -- never remove/rename existing
// functions, other templates may depend on them.
(function () {
  "use strict";

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  // --- Replo-style collapsible items (FAQ accordions built with the Replo --
  // --- page-builder app) -------------------------------------------------
  // Live-verified behavior: single-open accordion (opening one item closes
  // any other open sibling in the same list). Replo's own markup already
  // carries everything needed to identify and toggle these WITHOUT any
  // restructuring: an item wrapper `[data-replo-collapsible="true"]` with a
  // `data-state="closed"/"open"` attribute, a `[role=button]` question row
  // inside it (with its own `aria-expanded`), and a sibling answer container.
  // IMPORTANT: single-file's capture snapshot happens while every item is
  // closed, and this app conditionally-*mounts* the answer into the DOM
  // (React unmount, not a CSS display:none) -- so the answer container
  // survives capture as a genuinely EMPTY `<div hidden></div>`, with its
  // real text completely absent from raw.html, not just hidden. This is a
  // capture-fidelity gap distinct from the general "JS-injected content"
  // note elsewhere: there the element itself never exists; here the empty
  // shell exists but its content doesn't. Root-cause by clicking through
  // each item on the LIVE page and manually inserting the revealed answer
  // text into the matching empty container (marked `class=pc-faq-answer`
  // once filled in) before this function can have anything to toggle --
  // this function only handles the open/close mechanics, not sourcing the
  // missing content.
  function initReploCollapsible() {
    var items = document.querySelectorAll('[data-replo-collapsible="true"]');
    items.forEach(function (item) {
      var trigger = item.querySelector('[role=button]');
      var answer = item.querySelector(".pc-faq-answer");
      if (!trigger || !answer) return;
      trigger.addEventListener("click", function () {
        var isOpen = item.getAttribute("data-state") === "open";
        if (isOpen) {
          item.setAttribute("data-state", "closed");
          trigger.setAttribute("aria-expanded", "false");
          answer.hidden = true;
        } else {
          var siblings = item.parentElement.querySelectorAll('[data-replo-collapsible="true"]');
          siblings.forEach(function (sib) {
            if (sib === item) return;
            var sibTrigger = sib.querySelector("[role=button]");
            var sibAnswer = sib.querySelector(".pc-faq-answer");
            sib.setAttribute("data-state", "closed");
            if (sibTrigger) sibTrigger.setAttribute("aria-expanded", "false");
            if (sibAnswer) sibAnswer.hidden = true;
          });
          item.setAttribute("data-state", "open");
          trigger.setAttribute("aria-expanded", "true");
          answer.hidden = false;
        }
      });
    });
  }

  // --- Variant swatch selected-label sync --------------------------------
  // Presentation-layer behavior, not commerce: swatches/variant selectors
  // must visibly reflect the current selection like the original, even
  // though they don't drive real inventory/pricing here. Live-verified: a
  // `<variant-option-value for="optionN">` element displays the currently
  // selected option's label text (e.g. "Gold Color"), and updates the
  // instant a differently-named radio in that option group is checked --
  // matched by the radio's `name` (e.g. "option1") to the element's `for`.
  // The visible label text itself comes from the `.sr-only` span inside the
  // `<label for="{radio id}">` associated with the now-checked radio (the
  // swatch's own label is a sibling of the input, linked by for/id, not a
  // parent wrapping it -- do not assume `input.closest("label")` will find it).
  function initVariantLabelSync() {
    var radios = document.querySelectorAll('input[type=radio][name]');
    radios.forEach(function (input) {
      input.addEventListener("change", function () {
        if (!input.checked) return;
        var valueEl = document.querySelector('variant-option-value[for="' + input.name + '"]');
        if (!valueEl) return;
        var label = document.querySelector('label[for="' + input.id + '"]');
        var srText = label ? label.querySelector(".sr-only") : null;
        valueEl.textContent = srText ? srText.textContent.trim() : input.value;
      });
    });
  }

  // --- Sticky add-to-cart bar -------------------------------------------
  // Shows a fixed-position bar once the page's primary "Add to Cart" button
  // scrolls out of view, hides it again once that button is back in view.
  function initStickyBar() {
    var bar = document.querySelector("product-sticky-bar, [data-pc-sticky-bar]");
    var mainButton = document.querySelector(
      "buy-buttons:not(.sticky) button[type=submit], .product-form__buy-buttons button[type=submit], [data-pc-main-add-to-cart]"
    );
    if (!bar || !mainButton || !("IntersectionObserver" in window)) return;

    function show() {
      bar.style.visibility = "visible";
      bar.style.opacity = "1";
      bar.style.pointerEvents = "auto";
    }
    function hide() {
      bar.style.visibility = "hidden";
      bar.style.opacity = "0";
      bar.style.pointerEvents = "none";
    }
    hide();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) hide();
          else show();
        });
      },
      { threshold: 0 }
    );
    observer.observe(mainButton);
  }

  // --- Product gallery thumbnail switcher + position-indicator sync -------
  // Clicking a thumbnail or indicator dot scrolls the matching main-image
  // slide into view; swiping/scrolling the gallery itself keeps both the
  // thumbnail list and the indicator dots in sync with whichever slide is
  // actually centered. Matches thumbnails by `data-media-id` where available
  // (the theme's own pairing key -- robust against thumb/slide counts that
  // don't line up 1:1); indicator dots have no such id on this theme, so
  // they're matched by index against the carousel's direct-child slide list
  // (live-verified 1:1 count between dots/slides/thumbs on the audited page).
  // Live-verified behavior (do not change without re-checking against a live
  // page): only `aria-current` flips (on both the thumb and the matching dot)
  // -- the slide list's own classes never change, and the dot's black/white
  // fill is driven entirely by an existing `[aria-current]` CSS rule already
  // present in the captured stylesheet, not by anything this function styles
  // directly. Falls back to positional index matching against
  // `.swiper-slide`/`[data-pc-slide]` for themes without `data-media-id`.
  function initGalleryThumbnails() {
    var lists = document.querySelectorAll("[data-pc-gallery], .product-gallery");
    lists.forEach(function (gallery) {
      var thumbs = gallery.querySelectorAll(
        ".product-gallery__thumbnail-list [type=button], .product-gallery__thumbnail-list button, .product-gallery__thumbnail-list a"
      );
      var dots = gallery.parentElement
        ? gallery.parentElement.querySelectorAll("carousel-navigation .tap-area, [class*=page-dots] .tap-area")
        : [];
      if (!dots.length) dots = document.querySelectorAll("carousel-navigation .tap-area, [class*=page-dots] .tap-area");

      var carousel = gallery.querySelector(".product-gallery__carousel");
      var idSlides = carousel ? carousel.querySelectorAll("[data-media-id]") : [];
      var hasMediaIds = idSlides.length > 0 && thumbs.length && thumbs[0].hasAttribute("data-media-id");

      if (hasMediaIds) {
        var slideList = Array.prototype.slice.call(idSlides);

        function setActive(slide) {
          var id = slide.getAttribute("data-media-id");
          thumbs.forEach(function (t) {
            t.setAttribute("aria-current", t.getAttribute("data-media-id") === id ? "true" : "false");
          });
          var idx = slideList.indexOf(slide);
          dots.forEach(function (d, i) {
            d.setAttribute("aria-current", i === idx ? "true" : "false");
          });
        }

        thumbs.forEach(function (thumb) {
          thumb.addEventListener("click", function (e) {
            e.preventDefault();
            var id = thumb.getAttribute("data-media-id");
            var slide = carousel.querySelector('[data-media-id="' + id + '"]');
            if (!slide) return;
            setActive(slide);
            slide.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
          });
        });

        dots.forEach(function (dot, i) {
          dot.addEventListener("click", function (e) {
            e.preventDefault();
            var slide = slideList[i];
            if (!slide) return;
            setActive(slide);
            slide.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
          });
        });

        if (carousel && slideList.length) {
          var scrollTimer = null;
          carousel.addEventListener("scroll", function () {
            if (scrollTimer) clearTimeout(scrollTimer);
            scrollTimer = setTimeout(function () {
              var carouselRect = carousel.getBoundingClientRect();
              var carouselCenter = carouselRect.left + carouselRect.width / 2;
              var closest = slideList[0];
              var closestDist = Infinity;
              slideList.forEach(function (s) {
                var r = s.getBoundingClientRect();
                var dist = Math.abs(r.left + r.width / 2 - carouselCenter);
                if (dist < closestDist) {
                  closestDist = dist;
                  closest = s;
                }
              });
              setActive(closest);
            }, 120);
          });
        }
        return;
      }

      var slides = gallery.querySelectorAll(".swiper-slide, [data-pc-slide]");
      if (!slides.length) return;
      thumbs.forEach(function (thumb, i) {
        thumb.addEventListener("click", function (e) {
          e.preventDefault();
          slides.forEach(function (s) {
            s.classList.remove("swiper-slide-active");
          });
          if (slides[i]) {
            slides[i].classList.add("swiper-slide-active");
            slides[i].scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" });
          }
        });
      });
    });
  }

  // --- Widget card-strip prev/next scroll buttons -------------------------
  // For the .pc-widget-strip rebuilt carousels (see page-clone skill Step 3/10):
  // click a sibling [data-pc-prev]/[data-pc-next] button to scroll by one card.
  function initWidgetStripButtons() {
    document.querySelectorAll(".pc-widget-strip").forEach(function (strip) {
      var track = strip.querySelector(".pc-widget-track");
      if (!track) return;
      var firstCard = track.querySelector(".pc-widget-card");
      var cardWidth = firstCard ? firstCard.getBoundingClientRect().width + 12 : 200;
      var prev = strip.parentElement.querySelector("[data-pc-prev]");
      var next = strip.parentElement.querySelector("[data-pc-next]");
      if (prev) prev.addEventListener("click", function () { strip.scrollBy({ left: -cardWidth, behavior: "smooth" }); });
      if (next) next.addEventListener("click", function () { strip.scrollBy({ left: cardWidth, behavior: "smooth" }); });
    });
  }

  // --- Raw swiper-markup carousels (arrows + scroll-snap) -----------------
  // Handles leftover native `.swiper-wrapper` carousel markup: a theme
  // sometimes reuses swiper's class names for a secondary carousel that
  // isn't caught by a template-specific rebuild (e.g. a trust/press strip
  // sharing a class with the main gallery). CSS `overflow-x:auto` +
  // `scroll-snap-type` on `.swiper-wrapper` (added during cleanup) already
  // gives free touch/drag scroll; this just wires the prev/next buttons
  // on top of it and un-disables them (swiper's own JS normally does this).
  function initSwiperArrowScroll() {
    document.querySelectorAll(".swiper-wrapper").forEach(function (wrapper) {
      var slides = wrapper.querySelectorAll(".swiper-slide");
      if (!slides.length) return;
      var slideRect = slides[0].getBoundingClientRect();
      var gap = parseFloat(getComputedStyle(slides[0]).marginRight) || 0;
      var step = slideRect.width + gap;
      var container =
        wrapper.closest('[class*="swiper_slider"], [class*="swiper-slider"], .swiper') ||
        wrapper.parentElement;
      var prev = container.querySelector(".swiper-button-prev");
      var next = container.querySelector(".swiper-button-next");
      [prev, next].forEach(function (btn) {
        if (!btn) return;
        btn.classList.remove("swiper-button-disabled", "swiper-button-lock");
        btn.removeAttribute("tabindex");
        btn.removeAttribute("aria-disabled");
      });
      if (prev) prev.addEventListener("click", function () { wrapper.scrollBy({ left: -step, behavior: "smooth" }); });
      if (next) next.addEventListener("click", function () { wrapper.scrollBy({ left: step, behavior: "smooth" }); });
    });
  }

  // --- Prev/Next arrow-button carousels (native disabled-state pattern) ---
  // Handles `<button class="prev-next-button--prev/--next" disabled>` pairs
  // (common in Shopify "related products" style carousels): wires them to
  // scroll the nearest scrollable sibling by one card width, and keeps the
  // native `disabled` attribute in sync with scroll position so it matches
  // the live site's own boundary behavior instead of a stale initial state.
  function initPrevNextButtonScroll() {
    document.querySelectorAll('[class*="prev-next-button--prev"]').forEach(function (prevBtn) {
      var section = prevBtn.closest("section, [id], .section-stack") || document;
      var nextBtn = section.querySelector('[class*="prev-next-button--next"]');
      if (!nextBtn) return;
      var scroller = null;
      section.querySelectorAll("*").forEach(function (el) {
        if (scroller || el.children.length < 2) return;
        if (getComputedStyle(el).overflowX === "auto" && el.scrollWidth > el.clientWidth + 4) {
          scroller = el;
        }
      });
      if (!scroller) return;
      var firstCard = scroller.children[0];
      var gap = parseFloat(getComputedStyle(scroller).columnGap) || 0;
      var step = firstCard.getBoundingClientRect().width + gap;

      function syncDisabled() {
        prevBtn.disabled = scroller.scrollLeft <= 2;
        nextBtn.disabled = scroller.scrollLeft >= scroller.scrollWidth - scroller.clientWidth - 2;
      }
      prevBtn.addEventListener("click", function () { scroller.scrollBy({ left: -step, behavior: "smooth" }); });
      nextBtn.addEventListener("click", function () { scroller.scrollBy({ left: step, behavior: "smooth" }); });
      scroller.addEventListener("scroll", syncDisabled);
      syncDisabled();
    });
  }

  // --- Quantity/bundle tier selector (native radio-per-tier card, presentation
  // --- layer only) ---------------------------------------------------------
  // Selecting a tier restyles the chosen card via the native
  // `input:checked + label` CSS rule (no JS needed for that part) and updates
  // every PRICE/COMPARE_PRICE/SAVE_AMOUNT/SAVE_PERCENT-tokened element
  // sitewide to match the selected tier's numbers, so the page stays visually
  // consistent regardless of which tier a visitor has selected. Real cart/
  // variant wiring (actually changing what Add to Cart submits) is explicitly
  // NOT done here -- presentation-layer only, per standing instruction; that
  // remains the CC dev's job same as every other buy-button behavior in this
  // template.
  function setPriceLikeToken(el, newValue) {
    // Site-wide PRICE/COMPARE_PRICE/SAVE_AMOUNT instances all hold their
    // numeric value as the trailing text of a "$X.XX"-shaped fragment inside
    // the token element -- but some instances (e.g. the price-reminder box's
    // "Today's price: $69.99") bundle that fragment together with a label
    // prefix in the very same text node. Rather than assume one fixed shape,
    // find the first text-node child (searching from the end, since the
    // value is always last) that actually contains a $-amount, and replace
    // only that matched substring -- this leaves any label prefix and any
    // sibling elements (like a `.sr-only` "Sale price" span) untouched.
    for (var i = el.childNodes.length - 1; i >= 0; i--) {
      var node = el.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE && /\$[\d,]+(\.\d+)?/.test(node.textContent)) {
        // Use a replacer FUNCTION, not a plain string: a plain replacement
        // string containing a literal "$" followed by digits (e.g. "$119.85")
        // gets misinterpreted by String.replace as a $1-style capture-group
        // backreference, silently corrupting the output. A function return
        // value is inserted verbatim, no special-pattern parsing.
        node.textContent = node.textContent.replace(/\$[\d,]+(\.\d+)?/, function () { return newValue; });
        return true;
      }
    }
    return false;
  }

  function setPercentLikeToken(el, newValue) {
    for (var i = el.childNodes.length - 1; i >= 0; i--) {
      var node = el.childNodes[i];
      if (node.nodeType === Node.TEXT_NODE && /[\d.]+/.test(node.textContent)) {
        node.textContent = node.textContent.replace(/[\d.]+/, function () { return newValue; });
        return true;
      }
    }
    return false;
  }

  function initTierSelector() {
    document.querySelectorAll(".tier-picker").forEach(function (picker) {
      var radios = picker.querySelectorAll('input[type="radio"]');
      if (!radios.length) return;

      function applyTier(radio) {
        var label = picker.querySelector('label[for="' + radio.id + '"]');
        if (!label) return;
        var price = label.querySelector(".tier-option__price");
        var compare = label.querySelector(".tier-option__compare");
        var save = label.querySelector(".tier-option__save");
        var priceText = price ? price.textContent.trim() : null;
        var compareText = compare ? compare.textContent.trim() : null;
        // The card's own save line reads e.g. "Save $69.95" -- pull just the
        // dollar amount back out for the sitewide SAVE_AMOUNT tokens, and
        // compute the percent off from price/compare directly rather than
        // trying to scrape it back out of card copy that doesn't state it.
        var saveAmountMatch = save ? save.textContent.match(/\$[\d,]+(\.\d+)?/) : null;
        var saveAmountText = saveAmountMatch ? saveAmountMatch[0] : null;

        if (priceText) {
          document.querySelectorAll('[data-token="PRICE"]').forEach(function (el) {
            setPriceLikeToken(el, priceText);
          });
        }
        if (compareText) {
          document.querySelectorAll('[data-token="COMPARE_PRICE"]').forEach(function (el) {
            setPriceLikeToken(el, compareText);
          });
        }
        if (saveAmountText) {
          document.querySelectorAll('[data-token="SAVE_AMOUNT"]').forEach(function (el) {
            setPriceLikeToken(el, saveAmountText);
          });
        }
        if (priceText && compareText) {
          var priceNum = parseFloat(priceText.replace(/[^0-9.]/g, ""));
          var compareNum = parseFloat(compareText.replace(/[^0-9.]/g, ""));
          if (compareNum > 0 && !isNaN(priceNum)) {
            var percentOff = Math.round(((compareNum - priceNum) / compareNum) * 1000) / 10;
            document.querySelectorAll('[data-token="SAVE_PERCENT"]').forEach(function (el) {
              setPercentLikeToken(el, String(percentOff));
            });
          }
        }
      }

      radios.forEach(function (radio) {
        radio.addEventListener("change", function () {
          if (radio.checked) applyTier(radio);
        });
      });
    });
  }

  ready(function () {
    initStickyBar();
    initGalleryThumbnails();
    initWidgetStripButtons();
    initSwiperArrowScroll();
    initPrevNextButtonScroll();
    initVariantLabelSync();
    initReploCollapsible();
    initTierSelector();
  });
})();
